import React, { useCallback, useMemo, useState } from 'react';
import { StepControls } from '~/common/step-controls';
import { useSelector } from 'react-redux';
import { selectNewSong } from '~/features/dashboard/store/selectors';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { Grid, Typography, Box, Paper, Drawer, Chip } from '@material-ui/core';
import { NamedForm } from '~/features/dashboard/components/add-song/components/named-form';

import css from './styles.module.scss';
import { newSongFormNextStep } from '~/features/dashboard/store/actions';
import { SongProjectTrack } from '~/models/music/new-song/newSongResponse.dto';
import classNames from 'classnames';
import fakeUserImagePNG from '~/img/fake-user-image.png';
import Avatar from '@material-ui/core/Avatar';
import { SynchTab, TrackSync } from '~/features/dashboard/components/song-details/coponents/SynchTab';
import { useRouteMatch } from 'react-router-dom';
import { VerticalPopver } from '~/features/dashboard/components/vertical-popover';
import { axiosInstance } from '~/api/config/axios.config';

interface Props {
    prevStep: () => void;
    goToRecordings: () => void;
}

type KeyTracksType = SongProjectTrack & {
    sync?: TrackSync;
};

export const Step_3: React.FC<Props> = ({ prevStep, goToRecordings }) => {
    const newSongState = useSelector(selectNewSong);
    const dispatch = useThunkDispatch();
    const match = useRouteMatch<{ creatorId: string }>();
    const creatorId = match.params.creatorId;
    const [syncs, setSyncs] = useState<TrackSync[]>([]);
    const fetchSyncData = () => {
        axiosInstance.get('/catalogue/projectCreator/synchsongtag', { params: { gSongRef: newSongState?.gSongRef } }).then(({ data }) => {
            setSyncs(data);
        });
    };

    const [drawerSyncOpen, setDrawerSyncOpen] = useState<boolean | string | number>(false);
    const closeDrawer = () => {
        setDrawerSyncOpen(false);
        fetchSyncData();
    };

    const onSubmit = useCallback(() => {
        dispatch(newSongFormNextStep());
    }, [dispatch]);

    const keyTracks = useMemo(
        (): KeyTracksType[] =>
            (newSongState?.songProjectTrack || [])
                .filter((t: SongProjectTrack) => !!t.isKeyTrack)
                .map(t => {
                    const sync = syncs.find(s => s.isrc === t.isrc);
                    console.log(t, 't');
                    return {
                        ...t,
                        sync,
                    };
                }),
        [newSongState, syncs]
    );

    const editSyncAction = (id: string | number) => () => {
        setDrawerSyncOpen(id); // TODO: set sync data
    };

    const renderTagsByType = (tags: TrackSync['tags'], type: string) =>
        tags.find(t => t.type === type)?.value.map((v, i) => <Chip className={css.tag} key={i} label={v.toUpperCase()} />);

    const syncCategories = ['Genre', 'Context', 'Mood', 'Era', 'Classification', 'Behaviour'];

    return (
        <NamedForm onSubmit={onSubmit}>
            {!!keyTracks?.length ? (
                keyTracks.map(t => (
                    <Box key={t.isrc} className={css.track}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Paper className={classNames(css.paper, css.contactsWrapper)} elevation={1}>
                                    <Paper className={classNames(css.paper, css.contactsContent)}>
                                        <VerticalPopver childClassName={css.editIcon} actionsList="edit" onClickEdit={goToRecordings} />
                                        <Avatar
                                            variant="square"
                                            className={css.avatar}
                                            alt={t.trackArtist || ''}
                                            src={`${process.env.REACT_APP_ASSET}${t.mainArtistImage}` || fakeUserImagePNG}
                                        />
                                        <Box className={css.contactsContainer}>
                                            <Typography variant="subtitle1" className={css.bold} align="left">
                                                Artist
                                            </Typography>
                                            <Typography variant="body1" align="left">
                                                {t.trackArtist}
                                            </Typography>
                                            <Typography variant="subtitle1" className={classNames(css.Typography, css.bold)} align="left">
                                                Label
                                            </Typography>
                                            <Typography variant="body2" align="left">
                                                {t.trackLabel}
                                            </Typography>
                                            <Typography variant="subtitle1" className={classNames(css.Typography, css.bold)} align="left">
                                                ISRC
                                            </Typography>
                                            <Typography variant={'body2'} className={'bold'}>
                                                {t.isrc}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                    <Box className={css.contactsMeta}>
                                        <Typography style={{ minHeight: '30px' }} variant={'subtitle1'} className={css.bold}>
                                            {t.trackTitle}
                                        </Typography>
                                        <div className={css.contactsButton}>
                                            <Typography style={{ marginRight: '10px' }} variant="caption" align="left">
                                                Main Writer
                                            </Typography>
                                            <Typography variant="subtitle2" align="left">
                                                {newSongState?.gMyWriterName}
                                            </Typography>
                                        </div>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper className={css.syncBlock}>
                                    {syncCategories.map((category, i) => (
                                        <Grid container key={i}>
                                            <Grid item xs={5} className={css.key}>
                                                <Typography>{category}</Typography>
                                            </Grid>
                                            <Grid item xs={7} className={css.value}>
                                                <Box>
                                                    {t.sync?.tags && renderTagsByType(t.sync.tags, category)}
                                                    {i === 0 && (
                                                        <VerticalPopver childClassName={css.editIcon} actionsList="edit" onClickEdit={editSyncAction(t.isrc)} />
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                ))
            ) : (
                <Typography>No Key Tracks available</Typography>
            )}
            <StepControls className={css.nextButton} onPrevClick={prevStep} />
            {newSongState?.gSongRef && (
                <Drawer
                    variant="persistent"
                    anchor={'right'}
                    open={!!drawerSyncOpen}
                    onClose={() => setDrawerSyncOpen(false)}
                    className={css.drawerStyle}
                    ModalProps={{ container: document.body }}
                >
                    {drawerSyncOpen && (
                        <SynchTab
                            projectId={creatorId}
                            syncData={syncs.find(s => s.isrc === drawerSyncOpen) || { songRef: newSongState.gSongRef, isrc: String(drawerSyncOpen), tags: [] }}
                            close={closeDrawer}
                        />
                    )}
                </Drawer>
            )}
        </NamedForm>
    );
};
